import type { StudioChoice } from '@base/shared';
import { AlertTriangle, CheckCircle2, Wand2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fixOf, reviewOf } from './state.ts';

/**
 * A conferência de contraste (ADR 0133, parte 5): cada texto difícil de ler, com o conserto
 * quando a cor dele se ajusta (desfaz-se como qualquer escolha). Usada na Cor e no Revisar.
 */
export function CheckList({
  value,
  onChange,
  readOnly,
}: {
  value: StudioChoice;
  onChange: (next: StudioChoice) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const { warnings } = reviewOf(value, '');
  if (!warnings.length)
    return (
      <p role="status" className="flex items-start gap-2 text-sm text-ok">
        <CheckCircle2 size={18} aria-hidden className="shrink-0" />
        {t('style.checkOk')}
      </p>
    );
  const fixes = warnings.map((w) => fixOf(value, w));
  const all = warnings.reduce<StudioChoice>((s, w) => fixOf(s, w) ?? s, value);
  return (
    <div role="status" className="flex flex-col gap-3">
      <ul className="flex flex-col gap-2 text-sm">
        {warnings.map((w, i) => (
          <li key={`${w.text}-${w.background}`} className="flex items-start gap-2">
            <AlertTriangle size={18} aria-hidden className="shrink-0 text-danger" />
            <span className="min-w-0 flex-1">
              {t('style.checkWarn', {
                text: t(`style.tokens.${w.text}`, { defaultValue: w.text }),
                background: t(`style.tokens.${w.background}`, { defaultValue: w.background }),
                ratio: w.ratio.toLocaleString(),
                minimum: w.minimum.toLocaleString(),
              })}
              {!fixes[i] && (
                <span className="block text-xs text-muted">{t('style.fixAccent')}</span>
              )}
            </span>
            {fixes[i] && !readOnly && (
              <button
                type="button"
                onClick={() => onChange(fixes[i]!)}
                className="shrink-0 rounded-md border border-rule px-2 py-1 text-xs hover:bg-tonal"
              >
                {t('style.fix')}
              </button>
            )}
          </li>
        ))}
      </ul>
      {!readOnly && fixes.filter(Boolean).length > 1 && (
        <button
          type="button"
          onClick={() => onChange(all)}
          className="flex items-center gap-1.5 self-start text-sm text-brand-text underline-offset-2 hover:underline"
        >
          <Wand2 size={14} aria-hidden />
          {t('style.fixAll')}
        </button>
      )}
    </div>
  );
}
